import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Reusable SEO component.
 * - Sets <title>, meta description, canonical, Open Graph, Twitter card,
 *   and (optionally) JSON-LD structured data for the current page.
 * - noindex=true adds a robots noindex,nofollow directive (for admin / success pages).
 * - Client-side cleanup: on mount + prop change we prune any singleton <head>
 *   tags (title, canonical, og:*, twitter:*, description, robots) that were
 *   baked into the pre-rendered HTML. react-helmet-async marks its own tags
 *   with `data-rh="true"`, so anything without that attribute is a stale
 *   snapshot leftover and MUST be removed — otherwise Google sees duplicate
 *   titles/canonicals/OG URLs (especially harmful when Emergent's
 *   crawler-cache renders the page via headless Chrome and captures both
 *   the snapshot + Helmet tags in one HTML document).
 */

// Selectors for head tags that must appear at most once per document.
const SINGLETON_SELECTORS = [
  'title',
  'link[rel="canonical"]',
  'meta[name="description"]',
  'meta[name="robots"]',
  'meta[name="twitter:card"]',
  'meta[name="twitter:title"]',
  'meta[name="twitter:description"]',
  'meta[name="twitter:image"]',
  'meta[property="og:type"]',
  'meta[property="og:site_name"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'meta[property="og:image"]',
  'meta[property="og:locale"]',
];

const pruneStalePrerenderTags = () => {
  if (typeof document === 'undefined') return;
  for (const sel of SINGLETON_SELECTORS) {
    const nodes = document.head.querySelectorAll(sel);
    if (nodes.length <= 1) continue;
    // Prefer Helmet-managed tags (data-rh="true"); remove any others.
    const helmetTags = [];
    const strayTags = [];
    nodes.forEach((n) => {
      (n.getAttribute('data-rh') === 'true' ? helmetTags : strayTags).push(n);
    });
    if (helmetTags.length > 0) {
      // Helmet owns at least one — drop every non-Helmet duplicate, and keep
      // only the LAST Helmet tag (Helmet itself is authoritative).
      strayTags.forEach((n) => n.remove());
      helmetTags.slice(0, -1).forEach((n) => n.remove());
    } else {
      // No Helmet tag yet — fall back to "last one wins" so at least we
      // don't ship duplicates to bots that render before Helmet mounts.
      Array.from(nodes).slice(0, -1).forEach((n) => n.remove());
    }
  }
};

const SITE_URL = 'https://idkcredit.com';
const DEFAULT_OG_IMAGE = `${SITE_URL}/social-share.png`; // 1200×630 branded image
// Google Search uses this string as the "site name" shown above the URL.
// Kept short and matches the visible logo/brand for maximum recognition.
const SITE_NAME = 'İnkişafa Dəstək Kreditləri';

export const SEO = ({
  title,
  description,
  path = '/',
  ogImage = DEFAULT_OG_IMAGE,
  noindex = false,
  jsonLd,
}) => {
  const url = `${SITE_URL}${path}`;

  // Re-run dedup whenever any SEO-driving prop changes so route transitions
  // keep the head clean too. Helmet mutates the head in useEffect, so we run
  // ours after a rAF to guarantee ordering.
  useEffect(() => {
    let raf1 = 0;
    let raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(pruneStalePrerenderTags);
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [title, description, path, noindex, ogImage]);

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content="az_AZ" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data (optional) */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

/* ---------- Reusable JSON-LD builders ---------- */

const ORG_NODE = {
  '@type': 'FinancialService',
  '@id': `${SITE_URL}/#organization`,
  name: 'İnkişafa Dəstək Kreditləri',
  legalName: 'İnkişafa Dəstək Kreditləri BOKT MMC',
  alternateName: ['İDK BOKT', 'İnkişafa Dəstək Kreditləri BOKT'],
  url: SITE_URL,
  logo: `${SITE_URL}/logo.png`,
  telephone: '+994 12 464 85 88',
  email: 'info@idkbokt.az',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'AZ',
    addressLocality: 'Bakı',
    addressRegion: 'Bakı',
  },
  openingHoursSpecification: {
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    opens: '09:00',
    closes: '18:00',
  },
  areaServed: 'AZ',
};

// Google Search "site name" is primarily driven by a WebSite entity.
// See https://developers.google.com/search/docs/appearance/site-names
const WEBSITE_NODE = {
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: 'İnkişafa Dəstək Kreditləri',
  alternateName: 'İDK BOKT',
  inLanguage: 'az-AZ',
  publisher: { '@id': `${SITE_URL}/#organization` },
};

export const buildOrganizationLd = () => ({
  '@context': 'https://schema.org',
  '@graph': [ORG_NODE, WEBSITE_NODE],
});

export const buildBreadcrumbLd = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`,
  })),
});

export const buildFaqLd = (faqItems) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqItems.map((q) => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: q.answer,
    },
  })),
});
